import { createRoot } from "react-dom/client";

import React from 'react';

// Apollo
import { ApolloProvider as ApolloHOCProvider } from 'react-apollo';
import { ApolloProvider as ApolloHooksProvider } from '@apollo/client';
import client from './apollo';

// Router
import { BrowserRouter } from 'react-router-dom';
import * as serviceWorker from './serviceWorker';
import store from './redux/store';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import App from './App';
import AppCrashFallback from './components/AppCrashFallback';
import { initClarity } from './utils/clarity';
import { installDomMutationGuard } from './utils/domMutationGuard';
import { isSentryEnabled } from './utils/sentryEnvironment';

// Stylesheets
import './globals.css';
import 'assets/stylesheets/index.sass';
import { Toaster } from "./components/ui/toast";

// Production guard rail: refuse to boot if a local-emulator endpoint
// is set in a production build. Without this, an accidentally-leaked
// REACT_APP_S3_ENDPOINT in a production build env would silently
// redirect every upload to a sandbox URL with no visible failure
// until somebody noticed missing files. Mirrors the backend guard in
// `config/initializers/aws.rb`. Fires before Sentry init so the trace
// surfaces against a known-bad state, not against downstream symptoms.
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_S3_ENDPOINT) {
  throw new Error(
    'REACT_APP_S3_ENDPOINT is set in a production build. Refusing to boot. ' +
    'Production must use real AWS — unset REACT_APP_S3_ENDPOINT or use a ' +
    'non-production NODE_ENV.'
  );
}

if (
  isSentryEnabled(
    process.env.REACT_APP_ENV,
    process.env.REACT_APP_SENTRY_ENABLED
  )
) {
  Sentry.init({
    dsn: 'https://4d5b8fcd0a7c4e6cb0a54d69827aa90b@o1199815.ingest.sentry.io/6323689',
    integrations: [new BrowserTracing()],
    tracesSampleRate: process.env.REACT_APP_SENTRY_TRACE_RATE || 1.0,
    environment: process.env.REACT_APP_ENV
  });

  Sentry.setContext(
    'extra', {
      platform: process.env.REACT_APP_UNIVERSITY === 'DUOC' ? 'DUOC' : 'UC'
    }
  );
}

initClarity();

// Before the first render: React must never touch the DOM while it is unguarded.
// Tagged on Sentry rather than reported as an error — a mutated DOM is a condition of
// the session, not an incident, and the point is to see how many users browse
// translated, not to spend quota on one event per node.
installDomMutationGuard({
  onGuarded: (method) => {
    Sentry.setTag('dom_mutated_externally', true);
    Sentry.addBreadcrumb({
      category: 'dom',
      level: 'warning',
      message: `DOM mutation guard caught ${method} on an externally moved node`,
    });
  },
});

createRoot(document.getElementById("root")).render(
<ApolloHOCProvider client={client}>
  <ApolloHooksProvider client={client}>
    <Provider store={store}>
      <BrowserRouter>
        {/*
          A render error anywhere in App used to unmount the whole tree and leave an
          empty #root — the blank screen users reported. The only boundary in the
          codebase was ChartErrorBoundary, scoped to one dashboard.

          It wraps App rather than living inside it so it also catches failures in
          App's own providers, and sits inside BrowserRouter so the fallback keeps
          router context. The Toaster stays outside: a crashing toast should not be
          able to take the application down with it.
        */}
        <Sentry.ErrorBoundary fallback={({ error }) => <AppCrashFallback error={error} />}>
          <App />
        </Sentry.ErrorBoundary>
      </BrowserRouter>
      <Toaster position="bottom-center" />
    </Provider>
  </ApolloHooksProvider>
</ApolloHOCProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
